import { isSameDay, format } from "date-fns";

import { TFunction } from "i18next";

import { Link } from "react-router-dom";

import {
	faCalendar,
	faChevronDoubleDown,
	faEnvelope,
	faEnvelopeOpen,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import { ConversationMessageType } from "@/enum/conversation.enum";

import { sentBySuffix } from "./conversationProvenance";

import { ListPerson } from "../../../schema/list";
import { CcRecipientsLine } from "../components/CcRecipientsLine";
import { ThreadSystemPill } from "../components/ThreadSystemPill";

/** Two-letter uppercase initials from a first/last name pair, or null. */
const getInitials = (firstName?: string | null, lastName?: string | null): string | null => {
	const first = firstName?.trim()?.[0];
	const last = lastName?.trim()?.[0];

	if (!first || !last) {
		return null;
	}

	return `${first}${last}`.toUpperCase();
};

export const renderSentAt = (sentAt: string) => {
	return isSameDay(new Date(sentAt), new Date()) ? (
		<span className="font-semibold">at {format(new Date(sentAt), "HH:mm")}</span>
	) : (
		<span className="font-semibold">at {format(new Date(sentAt), "HH:mm dd.MM.yyyy")}</span>
	);
};

export const renderScheduledLabel = (scheduledFor: string | null) => {
	if (!scheduledFor) {
		return (
			<span className="flex items-center gap-1 text-sm text-text-meta">
				<Icon icon={faCalendar} /> Scheduled
			</span>
		);
	}

	return (
		<span className="flex items-center gap-1">
			<Icon icon={faCalendar} /> Scheduled for{" "}
			{isSameDay(new Date(scheduledFor), new Date())
				? format(new Date(scheduledFor), "HH:mm")
				: format(new Date(scheduledFor), "HH:mm dd.MM.yyyy")}
		</span>
	);
};

/** The manual-send step id: a message typed by hand, not produced by a campaign step. */
const MANUAL_STEP_ID = "n99";

/**
 * "sent an InMail" / "sent an InMail manually" for an InMail row, null for a chat message
 * (PREC-4091). An InMail reaches a prospect who is not connected, which is exactly the fact a
 * reader needs when a connection request is still pending beside it.
 */
export const inmailSentLabel = (
	conversation: Pick<Conversation, "linkedinMessageKind" | "stepId">,
	t?: TFunction
): string | null => {
	if (conversation.linkedinMessageKind !== "inmail") {
		return null;
	}

	const manual = conversation.stepId === MANUAL_STEP_ID;

	if (t) {
		return t(manual ? "inbox.thread.inmailSentManually" : "inbox.thread.inmailSent");
	}

	return manual ? "sent an InMail manually" : "sent an InMail";
};

/**
 * The sender line above a sent message.
 *
 * `currentUserId` decides whether the sender is "You" (PREC-3908). Without it every sent message
 * claimed to be yours, which is wrong exactly when it matters — a lead contacted from two
 * Signalist users, where the useful answer is which colleague sent it.
 */
export const renderMessageSent = (
	conversation: Conversation,
	scheduledFor?: string | null,
	t?: TFunction,
	currentUserId?: number | null
) => {
	const youSuffix = sentBySuffix(conversation, currentUserId);
	const inmailLabel = inmailSentLabel(conversation, t);

	if (conversation.type === "mail_sent") {
		return (
			<div className="text-sm text-text-meta font-semibold flex items-center gap-1">
				<Icon icon={conversation.openedAt ? faEnvelopeOpen : faEnvelope} />
				{conversation.email?.senderFirstName ?? conversation.senderName}{" "}
				{conversation.email?.senderLastName ?? ""} ({conversation.senderEmail}
				{youSuffix ? ` | ${youSuffix}` : ""}) {renderSentAt(conversation.sentAt)}
			</div>
		);
	}

	if (conversation.type === "mail_scheduled") {
		return (
			<div className="text-sm text-text-meta font-semibold flex flex-row items-center gap-1">
				<Icon icon={faEnvelope} />
				{conversation.email?.senderFirstName ?? conversation.senderName}{" "}
				{conversation.email?.senderLastName ?? ""} ({conversation.senderEmail}
				{youSuffix ? ` | ${youSuffix}` : ""}) {renderScheduledLabel(scheduledFor ?? null)}
			</div>
		);
	}

	if (
		conversation.type === "message_sent" &&
		conversation.messageType === ConversationMessageType.ACTION &&
		conversation.message !== "Invitation accepted"
	) {
		return (
			<details className="group w-full">
				<summary className="cursor-pointer  flex bg-white text-text-meta font-semibold p-2 rounded-lg mb-2">
					<div className="text-sm text-text-meta font-semibold flex items-center gap-2">
						<Link target="_blank" to={conversation.senderLinkedinUrl || ""}>
							<LinkedinIcon />
						</Link>
						<span>Linkedin Invite sent - {renderSentAt(conversation.sentAt)}</span>
						<Icon className="w-3 h-3 group-open:rotate-180" icon={faChevronDoubleDown} />
					</div>
				</summary>
				<div className="text-sm text-text-meta flex items-center py-3 px-4 rounded-lg text-sm inline-flex  flex-col bg-gray-200 whitespace-pre-wrap">
					{conversation.message}
				</div>
			</details>
		);
	}

	if (
		conversation.type === "message_scheduled" &&
		conversation.messageType === ConversationMessageType.ACTION &&
		conversation.message !== "Invitation accepted"
	) {
		return (
			<details className="group w-full">
				<summary className="cursor-pointer  flex bg-white text-text-meta font-semibold p-2 rounded-lg mb-2">
					<div className="text-sm text-text-meta font-semibold flex items-center gap-2">
						<Link target="_blank" to={conversation.senderLinkedinUrl || ""}>
							<LinkedinIcon />
						</Link>
						<span>Linkedin Invite scheduled</span>
						<Icon className="w-3 h-3 group-open:rotate-180" icon={faChevronDoubleDown} />
					</div>
				</summary>
				<div className="text-sm text-text-meta flex items-center py-3 px-4 rounded-lg text-sm inline-flex  flex-col bg-gray-200 whitespace-pre-wrap">
					{conversation.message}
				</div>
			</details>
		);
	}

	if (
		conversation.type === "message_sent" &&
		conversation.messageType === ConversationMessageType.ACTION &&
		conversation.message === "Invitation accepted"
	) {
		const connectedInitials = getInitials(
			conversation.user?.firstName,
			conversation.user?.lastName
		);
		const connectedName =
			conversation.user?.firstName && conversation.user?.lastName
				? `${conversation.user.firstName} ${conversation.user.lastName}`
				: undefined;

		return (
			<ThreadSystemPill
				connectedInitials={connectedInitials}
				connectedTitle={connectedName}
				icon={<LinkedinIcon />}
				label={t ? t("inbox.thread.linkedinInviteAccepted") : "Accepted your LinkedIn invite"}
				timestamp={renderSentAt(conversation.sentAt)}
			/>
		);
	}

	if (
		conversation.type === "message_sent" &&
		conversation.messageType === ConversationMessageType.MESSAGE
	) {
		return (
			<div className="text-sm text-text-meta font-semibold flex items-center">
				<Link target="_blank" to={conversation.senderLinkedinUrl || ""}>
					<LinkedinIcon />
				</Link>
				<span>
					{conversation.user?.firstName} {conversation.user?.lastName}
					{!conversation.isOtherAccount && youSuffix ? ` (${youSuffix})` : ""}{" "}
					{inmailLabel ? `${inmailLabel} ` : ""}
					{renderSentAt(conversation.sentAt)}
				</span>
			</div>
		);
	}

	if (
		conversation.type === "message_scheduled" &&
		conversation.messageType === ConversationMessageType.MESSAGE
	) {
		return (
			<div className="text-sm text-text-meta font-semibold flex items-center gap-1">
				<Link target="_blank" to={conversation.senderLinkedinUrl || ""}>
					<LinkedinIcon />
				</Link>
				<span className="text-sm text-text-meta font-semibold flex flex-row items-center gap-1">
					{conversation.user?.firstName} {conversation.user?.lastName}
					{!conversation.isOtherAccount && youSuffix ? ` (${youSuffix})` : ""}
					{renderScheduledLabel(scheduledFor ?? null)}
				</span>
			</div>
		);
	}

	return <></>;
};

export const renderMessageReceived = (conversation: Conversation, listPerson: ListPerson) => {
	if (conversation.type === "mail_received") {
		return (
			<div className="flex flex-col gap-1">
				<div className="text-sm text-text font-semibold flex items-center gap-1">
					<Icon icon={conversation.openedAt ? faEnvelopeOpen : faEnvelope} />
					{conversation.senderName || listPerson?.prospect?.name} ({conversation.senderEmail})
					{renderSentAt(conversation.sentAt)}
				</div>
				<CcRecipientsLine ccReceiverEmail={conversation.ccReceiverEmail} />
			</div>
		);
	}

	if (conversation.type === "message_received") {
		return (
			<div className="text-sm text-text-meta font-semibold flex items-center">
				<Link target="_blank" to={conversation.senderLinkedinUrl || ""}>
					<LinkedinIcon />
				</Link>
				<span>
					{conversation.senderName || listPerson?.prospect?.name}{" "}
					{conversation.senderEmail ? `(${conversation.senderEmail})` : ""}{" "}
					{renderSentAt(conversation.sentAt)}
				</span>
			</div>
		);
	}

	return <></>;
};
